import type { Bounds } from "@/engine/coords.ts";
import { WORLD_UNITS } from "@/engine/units.ts";
import { DEFAULT_DIVISIONS, type GridConfig } from "@/engine/grid.ts";

/**
 * Map registry.
 *
 * WARDOGS ships regions (Kavkazi, Europe, North America), each played as a
 * rotation over one of its towns. Every rotation has its own faction spawns
 * and safe zones, so a rotation — not a region — is the unit a session plans
 * around.
 *
 * The full render always covers 163.84 units per side. `playable` is the
 * narrower rectangle the in-game grid and coordinate readout use; only
 * Bakurani's is confirmed. Everything else falls back to the full extent and
 * is flagged so the UI can say so rather than quietly lie about scale.
 */

export type RegionId = "kavkazi" | "europe" | "north-america";

export interface Region {
  readonly id: RegionId;
  readonly label: string;
}

export const REGIONS: readonly Region[] = [
  { id: "kavkazi", label: "Kavkazi" },
  { id: "europe", label: "Europe" },
  { id: "north-america", label: "North America" },
];

export interface TheatreMap {
  readonly id: string;
  readonly label: string;
  readonly region: RegionId;
  /** Extent covered by the full map render, in game units. */
  readonly tileBounds: Bounds;
  /** Playable / searchable coordinate extent used by the in-game grid. */
  readonly playable: Bounds;
  /** True when `playable` is a guess rather than confirmed calibration. */
  readonly playableIsEstimated: boolean;
  /** True once map imagery has been wired up for this rotation. */
  readonly hasImagery: boolean;
  readonly notes?: string;
}

const FULL_EXTENT: Bounds = { minX: 0, maxX: WORLD_UNITS, minY: 0, maxY: WORLD_UNITS };

/**
 * The tile pyramid is rendered over very slightly more than the nominal world,
 * so the imagery bleeds a hair past 0 and 163.84. Using the real figures keeps
 * tiles pixel-aligned with coordinates instead of drifting by a few meters
 * across the full 16 km.
 */
const TILE_EXTENT: Bounds = { minX: -0.03, maxX: 163.81, minY: -0.01, maxY: 163.83 };

/** Local mirror of the tile pyramid. See scripts/fetch-tiles.mjs. */
export const TILE_BASE_PATH = "/maps/tiles";
export const TILE_SIZE = 256;
export const TILE_MIN_ZOOM = 0;
/** Zoom 5 is 32x32 tiles over 16.384 km, about 2 m per pixel. */
export const TILE_MAX_ZOOM = 5;

export const MAPS: readonly TheatreMap[] = [
  {
    id: "bakurani",
    label: "Bakurani",
    region: "kavkazi",
    tileBounds: TILE_EXTENT,
    playable: { minX: 23.35, maxX: 133.6, minY: 19.34, maxY: 129.65 },
    playableIsEstimated: false,
    hasImagery: true,
  },
  {
    id: "ozeti",
    label: "Ozeti",
    region: "europe",
    tileBounds: TILE_EXTENT,
    playable: { minX: 57.58, maxX: 143.07, minY: 21.81, maxY: 99.56 },
    playableIsEstimated: false,
    hasImagery: true,
  },
  {
    id: "zestafona",
    label: "Zestafona",
    region: "kavkazi",
    tileBounds: TILE_EXTENT,
    playable: { minX: 19.9, maxX: 124.89, minY: 50.7, maxY: 141.9 },
    playableIsEstimated: false,
    hasImagery: true,
    notes: "Region assignment inferred from the name; verify in game.",
  },
  {
    id: "mindori",
    label: "Mindori",
    region: "kavkazi",
    tileBounds: TILE_EXTENT,
    playable: FULL_EXTENT,
    playableIsEstimated: true,
    hasImagery: false,
    notes: "Rotation known; no imagery or calibration yet.",
  },
  {
    id: "tevza",
    label: "Tevza",
    region: "kavkazi",
    tileBounds: TILE_EXTENT,
    playable: FULL_EXTENT,
    playableIsEstimated: true,
    hasImagery: false,
    notes: "Rotation known; no imagery or calibration yet.",
  },
];

/** URL of a single tile in the local mirror. */
export function tileUrl(mapId: string, zoom: number, x: number, y: number): string {
  return `${TILE_BASE_PATH}/${mapId}/zoom_${zoom}/${x}_${y}.webp`;
}

export const DEFAULT_MAP_ID = "bakurani";

const MAPS_BY_ID = new Map<string, TheatreMap>(MAPS.map((map) => [map.id, map]));

export function getMap(id: string): TheatreMap | undefined {
  return MAPS_BY_ID.get(id);
}

export function getMapOrDefault(id: string | undefined): TheatreMap {
  const found = id ? MAPS_BY_ID.get(id) : undefined;
  if (found) return found;
  const fallback = MAPS_BY_ID.get(DEFAULT_MAP_ID);
  if (!fallback) throw new Error("Default map is missing from the registry");
  return fallback;
}

export function mapsByRegion(region: RegionId): TheatreMap[] {
  return MAPS.filter((map) => map.region === region);
}

/**
 * The callout grid.
 *
 * Laid over the full render rather than the playable rectangle, because the
 * playable area differs per rotation and a grid reference has to mean the same
 * thing everywhere. Sixteen cells across 163.84 units gives the game's own
 * one-kilometre cells.
 */
export function gridConfigFor(map: TheatreMap): GridConfig {
  return { bounds: map.tileBounds, divisions: DEFAULT_DIVISIONS };
}
